// Mirrors the sign-in session into a cookie on the parent domain (e.g. .podgo.io)
// so every subdomain can pick it up. localStorage is per-origin, which is why
// the auto-generated client alone can't share sessions across subdomains.
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

const COOKIE = 'podgo_auth';
const CHUNK = 3000;
const MAX_CHUNKS = 5;
const DEFAULT_DOMAIN = '.podgo.io';

interface SharedTokens {
  access_token: string;
  refresh_token: string;
  expires_at?: number;
}

/** Cookie domain for this host, or null when sharing doesn't apply (preview, localhost). */
function cookieDomain(): string | null {
  if (typeof window === 'undefined') return null;
  const configured = (import.meta.env.VITE_SHARED_AUTH_COOKIE_DOMAIN as string | undefined) ?? DEFAULT_DOMAIN;
  if (!configured || configured === 'off') return null;
  const bare = configured.replace(/^\./, '');
  const host = window.location.hostname;
  return host === bare || host.endsWith(`.${bare}`) ? `.${bare}` : null;
}

function writeRaw(name: string, value: string, domain: string, maxAge: number) {
  document.cookie = `${name}=${value}; Domain=${domain}; Path=/; Max-Age=${maxAge}; Secure; SameSite=Lax`;
}

function readRaw(name: string): string | null {
  const hit = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return hit ? hit.slice(name.length + 1) : null;
}

export function clearSharedAuthCookie() {
  const domain = cookieDomain();
  if (!domain) return;
  for (let i = 0; i < MAX_CHUNKS; i++) writeRaw(`${COOKIE}.${i}`, '', domain, 0);
}

function writeSharedAuthCookie(session: Session) {
  const domain = cookieDomain();
  if (!domain) return;
  const payload: SharedTokens = {
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    expires_at: session.expires_at,
  };
  const encoded = encodeURIComponent(JSON.stringify(payload));
  const chunks = encoded.match(new RegExp(`.{1,${CHUNK}}`, 'g')) ?? [];
  if (chunks.length > MAX_CHUNKS) return;
  // Refresh tokens outlive access tokens; keep the cookie for 30 days, rewritten on every refresh.
  const maxAge = 60 * 60 * 24 * 30;
  clearSharedAuthCookie();
  chunks.forEach((c, i) => writeRaw(`${COOKIE}.${i}`, c, domain, maxAge));
}

export function readSharedAuthCookie(): SharedTokens | null {
  if (!cookieDomain()) return null;
  let joined = '';
  for (let i = 0; i < MAX_CHUNKS; i++) {
    const part = readRaw(`${COOKIE}.${i}`);
    if (!part) break;
    joined += part;
  }
  if (!joined) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(joined)) as SharedTokens;
    return parsed.access_token && parsed.refresh_token ? parsed : null;
  } catch {
    return null;
  }
}

/** Called from the app's single onAuthStateChange listener. */
export function syncSharedAuthCookie(event: AuthChangeEvent, session: Session | null) {
  if (event === 'SIGNED_OUT') {
    clearSharedAuthCookie();
    return;
  }
  if (session) {
    writeSharedAuthCookie(session);
    return;
  }
  if (event === 'INITIAL_SESSION') void adoptSharedSession(null);
}

/** Adopts the shared-cookie session when it differs from the local one. */
async function adoptSharedSession(current: Session | null) {
  const shared = readSharedAuthCookie();
  if (!shared) {
    // Signed out on another subdomain.
    if (current && cookieDomain()) await supabase.auth.signOut({ scope: 'local' });
    return;
  }
  if (current?.refresh_token === shared.refresh_token || current?.access_token === shared.access_token) return;
  const { error } = await supabase.auth.setSession({
    access_token: shared.access_token,
    refresh_token: shared.refresh_token,
  });
  if (error) clearSharedAuthCookie();
}

/** Re-checks the shared cookie whenever the tab regains focus. Returns cleanup. */
export function watchSharedAuthCookie(): () => void {
  if (!cookieDomain()) return () => {};
  const onFocus = async () => {
    if (document.visibilityState !== 'visible') return;
    const { data } = await supabase.auth.getSession();
    await adoptSharedSession(data.session);
  };
  document.addEventListener('visibilitychange', onFocus);
  return () => document.removeEventListener('visibilitychange', onFocus);
}
